import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  GraduationCap, Plus, Trash2, Pencil, X, RefreshCw, Save,
  Download, ShieldAlert, CheckCircle, Circle, ClipboardList, Sparkles, FileText, Loader, Languages,
} from 'lucide-react';
import { useApp } from '../context/AppContext';

// Retraining — admin builder + assignment board.
//   • Quizzes tab: author topic quizzes (1 correct answer + N wrong ones). Seeded
//     with the Verbal Drop / Safe Drop starter set; edits save to the live KV copy.
//   • Assignments tab: assign a topic to a driver as a required task and watch the
//     board go from outstanding → certified. Certificates download here too.
// Route is admin-only in the nav; the body is gated again here and the backend
// function is adminLogin-gated, so a hand-typed URL still can't author anything.

const API = '/.netlify/functions/driver-training-sb';
const DRIVERS_API = '/.netlify/functions/drivers-sb';

const post = (url, body) => fetch(url, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
}).then((r) => r.json());

// Quiz text is a language map; the builder edits English at launch.
const en = (v) => (v && typeof v === 'object') ? (v.en || '') : (v || '');

function fmtWhen(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}
function b64ToBlob(base64, type) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: type || 'application/pdf' });
}

const blankOption = () => ({ text: { en: '' }, correct: false });
const blankQuestion = () => ({ id: '', prompt: { en: '' }, options: [ { text: { en: '' }, correct: true }, blankOption(), blankOption(), blankOption() ] });
const blankQuiz = () => ({ id: '', topic: '', active: true, description: { en: '' }, questions: [blankQuestion()] });

export default function RetrainingPage() {
  const { currentUser, addNotification } = useApp();
  const isAdmin = currentUser?.role === 'admin';

  const [tab, setTab] = useState('quizzes');
  const [quizzes, setQuizzes] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [status, setStatus] = useState({ assignments: [], records: [] });
  const [editing, setEditing] = useState(null);   // a quiz object being edited, or null
  const [genOpen, setGenOpen] = useState(false);  // "Generate from a document" modal
  const [busy, setBusy] = useState(false);

  const note = useCallback((msg, kind = 'info') => (addNotification ? addNotification(msg, kind) : undefined), [addNotification]);

  const loadQuizzes = useCallback(async () => {
    const d = await post(API, { action: 'listQuizzes' });
    if (d && d.success) setQuizzes(d.quizzes || []); else { setQuizzes([]); note(d?.error || 'Could not load quizzes', 'error'); }
  }, [note]);
  const loadStatus = useCallback(async () => {
    const d = await post(API, { action: 'listStatus' });
    if (d && d.success) setStatus({ assignments: d.assignments || [], records: d.records || [] });
  }, []);
  const loadDrivers = useCallback(async () => {
    const d = await post(DRIVERS_API, { action: 'listDrivers' });
    if (d && d.success) setDrivers((d.drivers || []).filter((x) => x.active !== false));
  }, []);

  useEffect(() => { if (isAdmin) { loadQuizzes(); loadStatus(); loadDrivers(); } }, [isAdmin, loadQuizzes, loadStatus, loadDrivers]);

  if (!isAdmin) {
    return (
      <>
        <div className="page-header"><div className="page-header-left"><h2>Retraining</h2></div></div>
        <div className="page-body">
          <div className="card" style={{ padding: 28, display: 'flex', alignItems: 'center', gap: 12 }}>
            <ShieldAlert size={22} color="var(--hp-gold)" />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Admins only</div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '.88rem' }}>The retraining builder is restricted to Paul and Jayda.</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── save / delete / assign handlers ─────────────────────────────────────────
  const saveQuiz = async (quiz) => {
    setBusy(true);
    try {
      const d = await post(API, { action: 'saveQuiz', quiz });
      if (d && d.success) { note('Quiz saved', 'success'); setEditing(null); await loadQuizzes(); }
      else note(d?.error || 'Could not save quiz', 'error');
    } finally { setBusy(false); }
  };
  const deleteQuiz = async (id, topic) => {
    if (!window.confirm(`Delete the "${topic}" quiz? Certificates already earned are kept.`)) return;
    const d = await post(API, { action: 'deleteQuiz', id });
    if (d && d.success) { note('Quiz deleted', 'success'); await loadQuizzes(); } else note(d?.error || 'Delete failed', 'error');
  };
  const toggleActive = async (quiz) => { await saveQuizQuiet({ ...quiz, active: !(quiz.active !== false) }); };
  const saveQuizQuiet = async (quiz) => {
    const d = await post(API, { action: 'saveQuiz', quiz });
    if (d && d.success) await loadQuizzes(); else note(d?.error || 'Could not update', 'error');
  };
  const restoreDefaults = async () => {
    if (!window.confirm('Restore the Verbal Drop and Safe Drop starter quizzes to their original questions? Your other quizzes are untouched.')) return;
    const d = await post(API, { action: 'restoreDefaults' });
    if (d && d.success) { note('Starter quizzes restored', 'success'); await loadQuizzes(); } else note(d?.error || 'Restore failed', 'error');
  };

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GraduationCap size={22} style={{ color: 'var(--hp-gold)' }} /> Retraining
          </h2>
          <p>Author competency quizzes and assign retraining to drivers.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => { loadQuizzes(); loadStatus(); }} title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="page-body">
        {/* sub-tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid var(--hp-border)' }}>
          {[{ k: 'quizzes', label: 'Quizzes' }, { k: 'assignments', label: 'Assignments' }].map((s) => (
            <button key={s.k} onClick={() => setTab(s.k)}
              style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.04em', textTransform: 'uppercase',
                color: tab === s.k ? 'var(--hp-gold)' : 'var(--hp-dim)',
                borderBottom: tab === s.k ? '2px solid var(--hp-gold)' : '2px solid transparent', marginBottom: -1 }}>
              {s.label}
            </button>
          ))}
        </div>

        {tab === 'quizzes' && (
          editing
            ? <QuizEditor quiz={editing} busy={busy} onCancel={() => setEditing(null)} onSave={saveQuiz} />
            : <QuizList quizzes={quizzes} onNew={() => setEditing(blankQuiz())} onEdit={(q) => setEditing(JSON.parse(JSON.stringify(q)))}
                onDelete={deleteQuiz} onToggle={toggleActive} onRestore={restoreDefaults} onGenerate={() => setGenOpen(true)}
                onReload={loadQuizzes} note={note} />
        )}

        {genOpen && (
          <GenerateModal
            note={note}
            onClose={() => setGenOpen(false)}
            onDrafted={(draft) => { setGenOpen(false); setTab('quizzes'); setEditing(draft); }}
          />
        )}

        {tab === 'assignments' && (
          <Assignments quizzes={quizzes || []} drivers={drivers} status={status} onChanged={loadStatus} note={note} />
        )}
      </div>
    </>
  );
}

// ── Languages ──────────────────────────────────────────────────────────────
// The portal serves a quiz in whatever languages its text maps hold. Drivers can
// translate one themselves, but a language translated HERE is waiting for them —
// nobody stands in the depot watching a spinner before they can start.
const QUIZ_LANGS = [
  { code: 'es', native: 'Español', flag: '🇪🇸' },
  { code: 'zh', native: '中文', flag: '🇨🇳' },
  { code: 'mn', native: 'Монгол', flag: '🇲🇳' },
];

// One language for one quiz. The backend translates a SLICE per call (a whole quiz
// in a single call runs too close to the function timeout), so keep asking for the
// next slice, handing back the token that stops anyone else duplicating the work.
function LangChips({ quiz, onDone, note }) {
  const [busyCode, setBusyCode] = useState('');
  const [progress, setProgress] = useState(null);

  const run = async (code) => {
    setBusyCode(code); setProgress({ done: 0, total: (quiz.questions || []).length });
    try {
      let from = 0, token = '', guard = 0, retried = false;
      for (;;) {
        const d = await post(API, { action: 'translateQuiz', id: quiz.id, lang: code, from, token, force: true });
        if (!d || !d.success) {
          // One retry for this slice — see the portal tab: a second ask almost
          // always lands after a timeout or an unparseable reply.
          if (!retried && !d?.busy) { retried = true; continue; }
          note(d?.error || 'Could not translate', 'error'); break;
        }
        retried = false;
        setProgress({ done: d.translated || 0, total: d.total || 0 });
        if (d.done || d.cached) { note(`${quiz.topic} translated`, 'success'); break; }
        from = d.next; token = d.token || '';
        if (++guard > 20) break;   // slices are 2-4 questions (Mongolian is the small one)
      }
      await onDone();
    } finally { setBusyCode(''); setProgress(null); }
  };

  const state = quiz.langState || {};
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
      <Languages size={13} style={{ color: 'var(--hp-dim)' }} />
      {QUIZ_LANGS.map((L) => {
        const st = state[L.code] || 'missing';
        const tone = st === 'ready' ? 'var(--hp-green)' : st === 'stale' ? '#e5a13a' : 'var(--hp-dim)';
        const title = st === 'ready' ? 'Translated and current'
          : st === 'stale' ? 'The English has changed since this was translated — click to redo it'
          : 'Not translated yet — click to translate';
        return (
          <button key={L.code} onClick={() => run(L.code)} disabled={!!busyCode} title={title}
            style={{ border: `1px solid ${st === 'ready' ? 'var(--hp-green)' : 'var(--hp-border)'}`, borderRadius: 999,
              padding: '2px 9px', fontSize: '0.68rem', fontWeight: 700, cursor: busyCode ? 'wait' : 'pointer',
              background: st === 'ready' ? 'rgba(46,125,50,0.10)' : 'transparent', color: tone, fontFamily: 'var(--font-body)' }}>
            {L.flag} {L.native}
            {busyCode === L.code && progress ? ` · ${progress.done}/${progress.total}` : st === 'stale' ? ' · stale' : ''}
          </button>
        );
      })}
    </div>
  );
}

// ── Quiz list ──────────────────────────────────────────────────────────────
function QuizList({ quizzes, onNew, onEdit, onDelete, onToggle, onRestore, onGenerate, onReload, note }) {
  if (quizzes === null) return <div style={{ color: 'var(--hp-dim)' }}>Loading quizzes…</div>;
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={onNew}><Plus size={14} /> New quiz</button>
        <button className="btn btn-primary" onClick={onGenerate}><Sparkles size={14} /> Generate from a document</button>
        <button className="btn btn-secondary" onClick={onRestore}><RefreshCw size={14} /> Restore starter quizzes</button>
      </div>
      {!quizzes.length && <div className="card" style={{ padding: 24, color: 'var(--hp-dim)' }}>No quizzes yet. Create one, or restore the starter set.</div>}
      <div style={{ display: 'grid', gap: 12 }}>
        {quizzes.map((q) => {
          const active = q.active !== false;
          return (
            <div key={q.id} className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {q.topic}
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                    background: active ? 'rgba(46,125,50,0.15)' : 'var(--hp-surface)',
                    color: active ? 'var(--hp-green)' : 'var(--hp-dim)', border: '1px solid var(--hp-border)' }}>
                    {active ? 'Active' : 'Hidden'}
                  </span>
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                  {(q.questions || []).length} question{(q.questions || []).length === 1 ? '' : 's'}
                  {en(q.description) ? ` · ${en(q.description)}` : ''}
                </div>
                <LangChips quiz={q} onDone={onReload} note={note} />
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => onToggle(q)}>{active ? 'Hide' : 'Activate'}</button>
              <button className="btn btn-secondary btn-sm" onClick={() => onEdit(q)}><Pencil size={13} /> Edit</button>
              <button className="btn btn-secondary btn-sm" onClick={() => onDelete(q.id, q.topic)} title="Delete"><Trash2 size={13} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Quiz editor ──────────────────────────────────────────────────────────────
function QuizEditor({ quiz, busy, onCancel, onSave }) {
  const [q, setQ] = useState(quiz);

  const setTopic = (v) => setQ((p) => ({ ...p, topic: v }));
  const setDesc = (v) => setQ((p) => ({ ...p, description: { ...(p.description || {}), en: v } }));
  const setPrompt = (qi, v) => setQ((p) => { const qs = [...p.questions]; qs[qi] = { ...qs[qi], prompt: { ...(qs[qi].prompt || {}), en: v } }; return { ...p, questions: qs }; });
  const setOptText = (qi, oi, v) => setQ((p) => { const qs = [...p.questions]; const opts = [...qs[qi].options]; opts[oi] = { ...opts[oi], text: { ...(opts[oi].text || {}), en: v } }; qs[qi] = { ...qs[qi], options: opts }; return { ...p, questions: qs }; });
  const setCorrect = (qi, oi) => setQ((p) => { const qs = [...p.questions]; qs[qi] = { ...qs[qi], options: qs[qi].options.map((o, i) => ({ ...o, correct: i === oi })) }; return { ...p, questions: qs }; });
  const addOption = (qi) => setQ((p) => { const qs = [...p.questions]; qs[qi] = { ...qs[qi], options: [...qs[qi].options, blankOption()] }; return { ...p, questions: qs }; });
  const removeOption = (qi, oi) => setQ((p) => { const qs = [...p.questions]; let opts = qs[qi].options.filter((_, i) => i !== oi); if (!opts.some((o) => o.correct) && opts.length) opts = opts.map((o, i) => ({ ...o, correct: i === 0 })); qs[qi] = { ...qs[qi], options: opts }; return { ...p, questions: qs }; });
  const addQuestion = () => setQ((p) => ({ ...p, questions: [...p.questions, blankQuestion()] }));
  const removeQuestion = (qi) => setQ((p) => ({ ...p, questions: p.questions.filter((_, i) => i !== qi) }));

  const problems = useMemo(() => {
    const errs = [];
    if (!q.topic.trim()) errs.push('Give the quiz a topic name.');
    q.questions.forEach((qq, i) => {
      if (!en(qq.prompt).trim()) errs.push(`Question ${i + 1} needs a question.`);
      const filled = qq.options.filter((o) => en(o.text).trim());
      if (filled.length < 2) errs.push(`Question ${i + 1} needs at least two answers.`);
      if (qq.options.filter((o) => o.correct).length !== 1) errs.push(`Question ${i + 1} must have exactly one correct answer marked.`);
    });
    return errs;
  }, [q]);

  const submit = () => {
    // Drop blank options before saving; keep the correct flag intact.
    const cleaned = { ...q, questions: q.questions.map((qq) => ({ ...qq, options: qq.options.filter((o) => en(o.text).trim()) })) };
    onSave(cleaned);
  };

  const inputStyle = { width: '100%', padding: '9px 11px', fontSize: '0.9rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="btn btn-secondary" onClick={onCancel}><X size={14} /> Cancel</button>
        <button className="btn btn-primary" disabled={busy || problems.length > 0} onClick={submit}><Save size={14} /> {busy ? 'Saving…' : 'Save quiz'}</button>
      </div>

      {problems.length > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 16, borderColor: 'var(--hp-gold)' }}>
          <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 6, color: 'var(--hp-gold)' }}>Before you can save:</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--hp-dim)', fontSize: '0.8rem' }}>{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
        </div>
      )}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <label style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>Topic</label>
        <input style={{ ...inputStyle, marginTop: 6, fontWeight: 700 }} value={q.topic} placeholder="e.g. Verbal Drop" onChange={(e) => setTopic(e.target.value)} />
        <label style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginTop: 12, display: 'block' }}>Short description (optional)</label>
        <input style={{ ...inputStyle, marginTop: 6 }} value={en(q.description)} placeholder="One line shown to the driver before they start" onChange={(e) => setDesc(e.target.value)} />
      </div>

      {q.questions.map((qq, qi) => (
        <div key={qi} className="card" style={{ padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontWeight: 800, color: 'var(--hp-gold)' }}>Q{qi + 1}</span>
            <span style={{ flex: 1 }} />
            {q.questions.length > 1 && <button className="btn btn-secondary btn-sm" onClick={() => removeQuestion(qi)}><Trash2 size={12} /> Remove</button>}
          </div>
          <input style={inputStyle} value={en(qq.prompt)} placeholder="Type the question…" onChange={(e) => setPrompt(qi, e.target.value)} />
          <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', margin: '10px 0 6px' }}>Answers — click the circle to mark the one correct answer:</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {qq.options.map((o, oi) => (
              <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setCorrect(qi, oi)} title="Mark correct"
                  style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: o.correct ? 'var(--hp-green)' : 'var(--hp-dim)', display: 'flex' }}>
                  {o.correct ? <CheckCircle size={20} /> : <Circle size={20} />}
                </button>
                <input style={{ ...inputStyle, borderColor: o.correct ? 'var(--hp-green)' : 'var(--hp-border)' }}
                  value={en(o.text)} placeholder={`Answer ${oi + 1}`} onChange={(e) => setOptText(qi, oi, e.target.value)} />
                {qq.options.length > 2 && <button className="btn btn-secondary btn-sm" onClick={() => removeOption(qi, oi)} title="Remove answer"><X size={12} /></button>}
              </div>
            ))}
          </div>
          {qq.options.length < 6 && <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => addOption(qi)}><Plus size={12} /> Add answer</button>}
        </div>
      ))}

      <button className="btn btn-secondary" onClick={addQuestion}><Plus size={14} /> Add question</button>
    </div>
  );
}

// ── Assignments + status board ─────────────────────────────────────────────
function Assignments({ quizzes, drivers, status, onChanged, note }) {
  const [driverLogin, setDriverLogin] = useState('');
  const [quizId, setQuizId] = useState('');
  const [busy, setBusy] = useState(false);
  const activeQuizzes = quizzes.filter((q) => q.active !== false);
  const nameOf = useMemo(() => { const m = {}; drivers.forEach((d) => { m[d.login] = d.name || d.login; }); return m; }, [drivers]);

  const assign = async () => {
    if (!driverLogin || !quizId) { note('Pick a driver and a topic', 'error'); return; }
    setBusy(true);
    try {
      const d = await post(API, { action: 'assignQuiz', driverLogin, quizId });
      if (d && d.success) { note(d.already ? 'That driver already has this outstanding' : 'Retraining assigned', 'success'); setQuizId(''); await onChanged(); }
      else note(d?.error || 'Could not assign', 'error');
    } finally { setBusy(false); }
  };
  const unassign = async (id) => { const d = await post(API, { action: 'unassign', id }); if (d && d.success) await onChanged(); else note(d?.error || 'Could not remove', 'error'); };

  const downloadCert = async (recordId, topic) => {
    const d = await post(API, { action: 'getCert', recordId });
    if (!d || !d.success) { note(d?.error || 'Could not open certificate', 'error'); return; }
    const url = URL.createObjectURL(b64ToBlob(d.base64, d.contentType));
    const a = document.createElement('a'); a.href = url; a.download = d.filename || `Certificate-${topic}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const selStyle = { padding: '9px 11px', fontSize: '0.9rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)', minWidth: 180 };
  const th = { textAlign: 'left', fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)', padding: '8px 10px', borderBottom: '1px solid var(--hp-border)' };
  const td = { padding: '10px', borderBottom: '1px solid var(--hp-border)', fontSize: '0.85rem' };

  const outstanding = status.assignments.filter((a) => a.status === 'outstanding');
  const passed = status.assignments.filter((a) => a.status === 'passed');

  return (
    <div>
      <div className="card" style={{ padding: 16, marginBottom: 20 }}>
        <div style={{ fontWeight: 800, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><ClipboardList size={16} color="var(--hp-gold)" /> Assign retraining</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={selStyle} value={driverLogin} onChange={(e) => setDriverLogin(e.target.value)}>
            <option value="">Choose a driver…</option>
            {drivers.map((d) => <option key={d.login} value={d.login}>{d.name ? `${d.name} (${d.login})` : d.login}</option>)}
          </select>
          <select style={selStyle} value={quizId} onChange={(e) => setQuizId(e.target.value)}>
            <option value="">Choose a topic…</option>
            {activeQuizzes.map((q) => <option key={q.id} value={q.id}>{q.topic}</option>)}
          </select>
          <button className="btn btn-primary" disabled={busy} onClick={assign}><Plus size={14} /> Assign</button>
        </div>
      </div>

      <SectionHead label={`Outstanding (${outstanding.length})`} />
      {!outstanding.length && <Empty>No outstanding retraining — everyone's up to date.</Empty>}
      {outstanding.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto', marginBottom: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Driver</th><th style={th}>Topic</th><th style={th}>Assigned</th><th style={th}></th></tr></thead>
            <tbody>
              {outstanding.map((a) => (
                <tr key={a.id}>
                  <td style={td}>{nameOf[a.driverLogin] || a.driverLogin} <span style={{ color: 'var(--hp-dim)' }}>({a.driverLogin})</span></td>
                  <td style={td}>{a.topic}</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>{fmtWhen(a.assignedAt)}</td>
                  <td style={{ ...td, textAlign: 'right' }}><button className="btn btn-secondary btn-sm" onClick={() => unassign(a.id)} title="Remove"><X size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SectionHead label={`Certified (${status.records.length})`} />
      {!status.records.length && <Empty>No certificates earned yet.</Empty>}
      {status.records.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Driver</th><th style={th}>Topic</th><th style={th}>Passed</th><th style={th}>Certificate #</th><th style={th}></th></tr></thead>
            <tbody>
              {status.records.map((r) => (
                <tr key={r.id}>
                  <td style={td}><CheckCircle size={13} color="var(--hp-green)" style={{ verticalAlign: -2, marginRight: 5 }} />{nameOf[r.driverLogin] || r.driverLogin}</td>
                  <td style={td}>{r.topic}</td>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>{fmtWhen(r.passedAt)}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.certNo}</td>
                  <td style={{ ...td, textAlign: 'right' }}><button className="btn btn-secondary btn-sm" onClick={() => downloadCert(r.id, r.topic)}><Download size={12} /> Certificate</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {passed.length > 0 && (
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 12 }}>
          {passed.length} assigned retraining task{passed.length === 1 ? '' : 's'} completed.
        </div>
      )}
    </div>
  );
}

// ── Generate-from-a-document modal ─────────────────────────────────────────
// Extracts the PDF text in the browser (pdfjs, already bundled) and asks the
// backend to draft questions. The draft opens in the editor for review — nothing
// is saved until the admin clicks Save.
async function extractPdfText(file) {
  const buf = await file.arrayBuffer();
  const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
  GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  const pdf = await getDocument({ data: buf }).promise;
  let out = '';
  const maxPages = Math.min(pdf.numPages, 30);
  for (let i = 1; i <= maxPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    out += content.items.map((it) => it.str).join(' ') + '\n\n';
  }
  return out.trim();
}

function GenerateModal({ onClose, onDrafted, note }) {
  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(10);
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'reading' | 'generating'
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const acceptFile = (f) => {
    if (!f) return;
    if (!/pdf$/i.test(f.type) && !/\.pdf$/i.test(f.name)) { setError('That is not a PDF — choose a PDF file.'); return; }
    setFile(f); setError('');
  };

  const run = async () => {
    if (!file) { setError('Choose a PDF first.'); return; }
    setError('');
    try {
      setPhase('reading');
      const text = await extractPdfText(file);
      if (!text || text.length < 200) {
        setPhase('idle');
        setError('That PDF has very little selectable text (it may be a scan or photo). Use a text-based PDF, or add the questions manually.');
        return;
      }
      setPhase('generating');
      const d = await post(API, { action: 'generateQuiz', topic: topic.trim(), sourceText: text, count });
      if (d && d.success && d.quiz) {
        note && note(d.note || 'Draft ready — review and save', 'success');
        onDrafted(d.quiz);
      } else {
        setPhase('idle');
        setError(d?.error || 'Could not generate questions.');
      }
    } catch (e) {
      setPhase('idle');
      setError(e.message || 'Could not read that PDF.');
    }
  };

  const busy = phase !== 'idle';
  const inputStyle = { width: '100%', padding: '9px 11px', fontSize: '0.9rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)' };

  return (
    <div onClick={busy ? undefined : onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: '100%', maxWidth: 520, padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Sparkles size={18} color="var(--hp-gold)" />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', color: 'var(--hp-text)', letterSpacing: '0.03em' }}>Generate from a document</span>
        </div>
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', marginBottom: 16 }}>
          Drop in a procedure PDF and the app will draft scenario-based questions (with a light, humorous touch on the wrong answers). You review and edit everything before it saves — nothing goes to drivers automatically.
        </p>

        <label style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>Topic (optional)</label>
        <input style={{ ...inputStyle, marginTop: 6, marginBottom: 14 }} value={topic} placeholder="e.g. Delivered Location — leave blank to infer from the document" onChange={(e) => setTopic(e.target.value)} disabled={busy} />

        <label style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>Number of questions</label>
        <select style={{ ...inputStyle, marginTop: 6, marginBottom: 14 }} value={count} onChange={(e) => setCount(parseInt(e.target.value, 10))} disabled={busy}>
          {[5, 8, 10, 12, 15].map((n) => <option key={n} value={n}>{n} questions</option>)}
        </select>

        <label style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>Procedure PDF</label>
        <label
          onDragOver={(e) => { if (!busy) { e.preventDefault(); setDragOver(true); } }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!busy) acceptFile(e.dataTransfer.files && e.dataTransfer.files[0]); }}
          style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, padding: 18, borderRadius: 'var(--radius)', cursor: busy ? 'default' : 'pointer',
            border: `2px dashed ${dragOver ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
            background: dragOver ? 'rgba(201,168,76,0.10)' : 'var(--hp-surface)', transition: 'border-color 0.15s, background 0.15s' }}>
          <FileText size={18} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: '0.85rem', color: file ? 'var(--hp-text)' : 'var(--hp-dim)' }}>
            {file ? file.name : (dragOver ? 'Drop the PDF here…' : 'Drag a PDF here, or click to choose…')}
          </span>
          <input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} disabled={busy}
            onChange={(e) => acceptFile(e.target.files && e.target.files[0])} />
        </label>

        {error && <div style={{ color: 'var(--hp-red)', fontSize: '0.8rem', marginTop: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={run} disabled={busy || !file}>
            {busy ? <><Loader size={14} /> {phase === 'reading' ? 'Reading PDF…' : 'Drafting questions…'}</> : <><Sparkles size={14} /> Generate</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionHead({ label }) {
  return <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)', margin: '4px 0 10px' }}>{label}</div>;
}
function Empty({ children }) {
  return <div className="card" style={{ padding: 18, color: 'var(--hp-dim)', fontSize: '0.85rem', marginBottom: 24 }}>{children}</div>;
}
